import { useState, useEffect } from "react";
import { adminPlans, type PricingPlan } from "@/lib/api";
import Spinner from "@/components/Spinner";
import {
  Plus, Pencil, Trash2, X, Check, Star, Users,
  DollarSign, Save, AlertTriangle,
} from "lucide-react";
import clsx from "clsx";

const inp = "w-full px-3 py-2 rounded-xl border border-black/[0.08] bg-paper text-sm focus:outline-none focus:ring-2 focus:ring-coral/30 focus:border-coral/50 transition-all";

const EMPTY_PLAN: Partial<PricingPlan> = {
  slug: "", name: "", monthly_price: null, annual_price: null,
  subscriber_limit: null, monthly_email_limit: null,
  sort_order: 0, description: "", cta_label: "", cta_link: "/register",
  is_popular: 0, is_active: 1,
};

export default function AdminPlansPage() {
  const [plans, setPlans]       = useState<(PricingPlan & { user_count?: number })[]>([]);
  const [loading, setLoading]   = useState(true);
  const [editing, setEditing]   = useState<Partial<PricingPlan> | null>(null);
  const [isNew, setIsNew]       = useState(false);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState("");
  const [deleteSlug, setDeleteSlug] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    adminPlans.list()
      .then((d) => setPlans(d.plans))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const openNew = () => {
    setEditing({ ...EMPTY_PLAN });
    setIsNew(true);
    setError("");
  };

  const openEdit = (plan: PricingPlan) => {
    setEditing({ ...plan });
    setIsNew(false);
    setError("");
  };

  const close = () => { setEditing(null); setIsNew(false); setError(""); };

  const save = async () => {
    if (!editing) return;
    setSaving(true);
    setError("");
    try {
      if (isNew) {
        await adminPlans.create(editing);
      } else {
        await adminPlans.update(editing.slug!, editing);
      }
      close();
      load();
    } catch (e: any) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteSlug) return;
    setError("");
    try {
      await adminPlans.delete(deleteSlug);
      setDeleteSlug(null);
      load();
    } catch (e: any) {
      setError(e.message || "Delete failed");
    }
  };

  const set = (key: string, val: any) =>
    setEditing((prev) => prev ? { ...prev, [key]: val } : prev);

  if (loading) return <Spinner className="h-screen" />;

  return (
    <div>
      <div className="sticky top-0 z-10 bg-paper/90 backdrop-blur-md border-b border-black/[0.06] px-4 sm:px-6 lg:px-8 py-4 flex items-center justify-between">
        <div>
          <h1 className="text-lg sm:text-xl font-bold text-ink">Pricing Plans</h1>
          <p className="text-xs sm:text-sm text-slate mt-0.5">Manage tiers, pricing, and limits</p>
        </div>
        <button onClick={openNew}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark shadow-sm shadow-coral/20 transition-colors">
          <Plus size={15} /> Add plan
        </button>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-5xl mx-auto space-y-4">

        {error && !editing && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-5 py-3 text-sm flex items-center gap-2">
            <AlertTriangle size={15} /> {error}
          </div>
        )}

        {/* Plans grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {plans.map((plan) => (
            <div key={plan.slug}
              className={clsx(
                "relative bg-white rounded-2xl border p-5 transition-all",
                plan.is_active ? "border-black/[0.06]" : "border-dashed border-black/[0.12] opacity-60"
              )}>
              {!!plan.is_popular && (
                <Star size={14} className="absolute top-4 right-4 text-amber-400 fill-amber-400" />
              )}
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h3 className="font-bold text-ink text-base">{plan.name}</h3>
                  <p className="text-xs text-slate">{plan.slug}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button onClick={() => openEdit(plan)}
                    className="p-2 rounded-xl hover:bg-black/[0.04] text-slate hover:text-ink transition-colors">
                    <Pencil size={14} />
                  </button>
                  {plan.slug !== "starter" && (
                    <button onClick={() => setDeleteSlug(plan.slug)}
                      className="p-2 rounded-xl hover:bg-red-50 text-slate hover:text-red-500 transition-colors">
                      <Trash2 size={14} />
                    </button>
                  )}
                </div>
              </div>

              <p className="text-sm text-slate mb-4 line-clamp-2">{plan.description || "—"}</p>

              <div className="grid grid-cols-2 gap-3 text-xs">
                <div className="bg-black/[0.02] rounded-xl p-3">
                  <p className="text-slate">Monthly</p>
                  <p className="font-bold text-ink mt-0.5">
                    {plan.monthly_price !== null ? `$${plan.monthly_price}` : "Custom"}
                  </p>
                </div>
                <div className="bg-black/[0.02] rounded-xl p-3">
                  <p className="text-slate">Annual</p>
                  <p className="font-bold text-ink mt-0.5">
                    {plan.annual_price !== null ? `$${plan.annual_price}/mo` : "Custom"}
                  </p>
                </div>
                <div className="bg-black/[0.02] rounded-xl p-3">
                  <p className="text-slate">Sub limit</p>
                  <p className="font-bold text-ink mt-0.5">
                    {plan.subscriber_limit ? plan.subscriber_limit.toLocaleString() : "∞"}
                  </p>
                </div>
                <div className="bg-black/[0.02] rounded-xl p-3">
                  <p className="text-slate">Send limit</p>
                  <p className="font-bold text-ink mt-0.5">
                    {plan.monthly_email_limit ? plan.monthly_email_limit.toLocaleString() : "∞"}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between mt-4 pt-3 border-t border-black/[0.06]">
                <div className="flex items-center gap-1.5 text-xs text-slate">
                  <Users size={13} />
                  <span className="font-semibold">{(plan.user_count || 0).toLocaleString()}</span> users
                </div>
                <span className={clsx("text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full",
                  plan.is_active ? "bg-teal/10 text-teal" : "bg-black/[0.04] text-slate")}>
                  {plan.is_active ? "Active" : "Inactive"}
                </span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Edit / Create modal */}
      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-6 py-4 border-b border-black/[0.06]">
              <h2 className="font-bold text-ink text-lg">{isNew ? "New plan" : `Edit: ${editing.name}`}</h2>
              <button onClick={close} className="p-2 rounded-xl hover:bg-black/[0.04] text-slate"><X size={16} /></button>
            </div>

            <div className="px-6 py-5 space-y-4">
              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-4 py-2.5 text-sm">
                  {error}
                </div>
              )}

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Slug</label>
                  <input value={editing.slug || ""} onChange={(e) => set("slug", e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, ""))}
                    disabled={!isNew} className={clsx(inp, !isNew && "opacity-60")} placeholder="growth" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Name</label>
                  <input value={editing.name || ""} onChange={(e) => set("name", e.target.value)} className={inp} placeholder="Growth" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Monthly ($)</label>
                  <input type="number" step="0.01" value={editing.monthly_price ?? ""} placeholder="Leave empty for Custom"
                    onChange={(e) => set("monthly_price", e.target.value === "" ? null : parseFloat(e.target.value))} className={inp} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Annual $/mo</label>
                  <input type="number" step="0.01" value={editing.annual_price ?? ""} placeholder="Leave empty for Custom"
                    onChange={(e) => set("annual_price", e.target.value === "" ? null : parseFloat(e.target.value))} className={inp} />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Subscriber limit</label>
                  <input type="number" value={editing.subscriber_limit ?? ""} placeholder="Empty = unlimited"
                    onChange={(e) => set("subscriber_limit", e.target.value === "" ? null : parseInt(e.target.value))} className={inp} />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Monthly email limit</label>
                  <input type="number" value={editing.monthly_email_limit ?? ""} placeholder="Empty = unlimited"
                    onChange={(e) => set("monthly_email_limit", e.target.value === "" ? null : parseInt(e.target.value))} className={inp} />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Description</label>
                <input value={editing.description || ""} onChange={(e) => set("description", e.target.value)} className={inp}
                  placeholder="For growing businesses that need scale." />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">CTA label</label>
                  <input value={editing.cta_label || ""} onChange={(e) => set("cta_label", e.target.value)} className={inp}
                    placeholder="Start 14-day trial" />
                </div>
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">CTA link</label>
                  <input value={editing.cta_link || ""} onChange={(e) => set("cta_link", e.target.value)} className={inp}
                    placeholder="/register" />
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-bold uppercase tracking-wider text-slate mb-1">Sort order</label>
                  <input type="number" value={editing.sort_order ?? 0} onChange={(e) => set("sort_order", parseInt(e.target.value) || 0)} className={inp} />
                </div>
                <label className="flex items-center gap-2 cursor-pointer pt-5">
                  <input type="checkbox" checked={!!editing.is_popular}
                    onChange={(e) => set("is_popular", e.target.checked)} className="rounded border-black/20" />
                  <span className="text-sm text-ink font-medium">Popular badge</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer pt-5">
                  <input type="checkbox" checked={editing.is_active !== 0}
                    onChange={(e) => set("is_active", e.target.checked ? 1 : 0)} className="rounded border-black/20" />
                  <span className="text-sm text-ink font-medium">Active</span>
                </label>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-black/[0.06]">
              <button onClick={close}
                className="px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-ink hover:border-black/20 transition-colors">
                Cancel
              </button>
              <button onClick={save} disabled={saving || !editing.slug || !editing.name}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-coral text-white text-sm font-semibold hover:bg-coral-dark disabled:opacity-50 shadow-sm shadow-coral/20 transition-colors">
                <Save size={14} /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation */}
      {deleteSlug && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 space-y-4">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-red-50">
                <AlertTriangle size={18} className="text-red-500" />
              </div>
              <h3 className="font-bold text-ink">Delete plan "{deleteSlug}"?</h3>
            </div>
            <p className="text-sm text-slate">
              This will permanently remove this plan and all its feature rows. Users currently on this plan must be reassigned first.
            </p>
            {error && <p className="text-sm text-red-600 font-medium">{error}</p>}
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => { setDeleteSlug(null); setError(""); }}
                className="px-4 py-2.5 rounded-full border border-black/[0.08] text-sm font-semibold text-ink">
                Cancel
              </button>
              <button onClick={confirmDelete}
                className="px-5 py-2.5 rounded-full bg-red-500 text-white text-sm font-semibold hover:bg-red-600 transition-colors">
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
